import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, RotateCcw, Save, UserPen } from 'lucide-react';
import { Card } from '@components/base/layout';
import { Text, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Alert, Spinner } from '@components/base/feedback';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type {
  EntityFormConfig,
  FieldConfig,
  SelectFieldOptions,
  UseEntityFormReturn,
} from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromEntityForm,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { INTERVENANT_UPDATE_FIELDS, intervenantService } from '../api/intervenantService';
import type {
  CompanyOption,
  IntervenantOption,
  IntervenantUpdateValues,
  SinperRecord,
} from '../api/intervenantService';
import type { WorkflowExecutionError } from '../api/taskService';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { WorkflowStepTracker } from './WorkflowStepTracker';
import type { WorkflowStepExecutionSummary } from './WorkflowStepTracker';
import { useClaimDataStore } from './claimDataStore';

interface EditIntervenantCardProps {
  /**
   * Veos session id — part of the claim-card prop contract (registered prop);
   * the card's endpoints are keyed on the claim / intervenant ids only.
   */
  ses: string;
  /** Claim id (`ID_SIN`) — feeds the « Intervenant » select (`WS2_GS_SEL_IDSINP`). */
  idSin: string;
  /**
   * Tenant-scoped layout overrides, injected by the `withComponentRules` HOC
   * (do not pass by hand). With no rules authored this is `undefined` and the
   * render is the baseline. Its `fields` bucket targets the stable field keys
   * of {@link EDIT_INTERVENANT_FIELDS} (`idSinp`, `nom`, `prenom`, `email`,
   * `telephone`, `adresse1`, `adresse2`, `cp`, `ville`, `type`, `ref`, `cdCie`,
   * `numPolTiers`, `numSinTiers`, `risque1`), `actions.submit` the save button
   * and `title` the card title — all merged through `applyLayoutOverrides` /
   * `resolveCardTitle`.
   */
  _layoutOverrides?: LayoutOverrides;
}

// ---------------------------------------------------------------------------
// Declarative config — stable keys, labels / placeholders live here
// ---------------------------------------------------------------------------

const EDIT_INTERVENANT_COMPONENT_ID = 'claimflow.editIntervenantCard';

/** Stable key of the « Intervenant » select — its value is the `ID_SINP`, as a string. */
const ID_SINP_FIELD_KEY = 'idSinp';

/** Runtime texts of the card (never inline JSX strings). */
const EDIT_INTERVENANT_CARD_TEXT = {
  title: 'Modifier un intervenant',
  submit: 'Enregistrer les modifications',
  /** Disabled button + header recap after a successful save. */
  submitted: 'Intervenant modifié',
  /** Leaves the recap for a fresh edit form. */
  editAnother: 'Modifier un autre intervenant',
  /** « Intervenant » select placeholder while the claim's intervenants load. */
  optionsLoadingPlaceholder: 'Chargement des intervenants…',
  /** « Intervenant » select helper text once their fetch has failed. */
  optionsLoadError: 'Impossible de charger les intervenants du sinistre.',
  recordLoadErrorTitle: "Impossible de charger l'intervenant",
  recordLoadErrorFallback: "Le chargement de l'intervenant a échoué.",
  saveErrorTitle: 'La modification a échoué',
  saveErrorFallback: "La modification de l'intervenant a échoué.",
  stepsHeading: "Détail de l'exécution :",
} as const;

/**
 * The card's fields in mockup order — the same definitions / labels / types
 * as the create form (`IntervenantForm`), preceded by the « Intervenant »
 * select. Option lists (intervenants, compagnies) are injected by
 * {@link buildEditIntervenantFormConfig}.
 */
const EDIT_INTERVENANT_FIELDS: Record<string, FieldConfig> = {
  [ID_SINP_FIELD_KEY]: {
    type: 'select',
    label: 'Intervenant',
    order: 1,
    colSpan: 2,
    validation: { required: true },
    options: { searchable: true, placeholder: 'Sélectionner un intervenant' } as SelectFieldOptions,
  },
  nom: {
    type: 'text',
    label: 'Nom',
    order: 2,
    validation: { required: true },
    placeholder: 'Nom',
  },
  prenom: { type: 'text', label: 'Prénom', order: 3, placeholder: 'Prénom' },
  email: { type: 'email', label: 'Email', order: 4, placeholder: 'email@exemple.com' },
  telephone: { type: 'tel', label: 'Téléphone', order: 5, placeholder: '06 00 00 00 00' },
  adresse1: {
    type: 'text',
    label: 'Adresse 1',
    order: 6,
    colSpan: 2,
    placeholder: 'Adresse ligne 1',
  },
  adresse2: {
    type: 'text',
    label: 'Adresse 2',
    order: 7,
    colSpan: 2,
    placeholder: 'Adresse ligne 2',
  },
  cp: { type: 'text', label: 'CP', order: 8, placeholder: '75000' },
  ville: { type: 'text', label: 'Ville', order: 9, placeholder: 'Paris' },
  type: {
    type: 'select',
    label: 'Type',
    order: 10,
    options: {
      items: [
        { value: 'P', label: 'Physique' },
        { value: 'M', label: 'Morale' },
      ],
      placeholder: 'Sélectionner un type',
    } as SelectFieldOptions,
  },
  ref: { type: 'text', label: 'Ref.', order: 11, placeholder: 'Référence' },
  cdCie: {
    type: 'select',
    label: 'Compagnie',
    order: 12,
    options: { searchable: true, placeholder: 'Sélectionner une compagnie' } as SelectFieldOptions,
  },
  numPolTiers: { type: 'text', label: 'No de police', order: 13, placeholder: 'N° de police' },
  numSinTiers: {
    type: 'text',
    label: 'Ref sinistre tiers',
    order: 14,
    placeholder: 'Référence sinistre tiers',
  },
  risque1: { type: 'text', label: 'Immat', order: 15, placeholder: 'AA-000-AA' },
};

/** Inject a select's option items into its field config. */
function withItems(field: FieldConfig, items: { value: string; label: string }[]): FieldConfig {
  return {
    ...field,
    options: { ...(field.options as SelectFieldOptions | undefined), items } as SelectFieldOptions,
  };
}

function buildEditIntervenantFormConfig(
  intervenants: IntervenantOption[],
  companies: CompanyOption[],
): EntityFormConfig {
  const fields: Record<string, FieldConfig> = { ...EDIT_INTERVENANT_FIELDS };
  const idSinpField = fields[ID_SINP_FIELD_KEY];
  if (idSinpField) {
    fields[ID_SINP_FIELD_KEY] = withItems(
      idSinpField,
      intervenants.map((option) => ({ value: String(option.value), label: option.label })),
    );
  }
  const cdCieField = fields.cdCie;
  if (cdCieField) {
    fields.cdCie = withItems(cdCieField, companies);
  }

  return {
    id: 'edit-intervenant',
    endpoint: {
      erpEndpoint: '/claimflow/workflows/claimflow-update-stakeholder/execute',
      method: 'POST',
    },
    fields,
    actions: {
      submit: { visible: true, label: EDIT_INTERVENANT_CARD_TEXT.submit },
      cancel: { visible: false },
      reset: { visible: false },
    },
    layout: { columns: 2, gap: 'sm' },
  };
}

type OptionsLoadState = 'loading' | 'ready' | 'error';

/**
 * Overlay the runtime state on the (override-merged) config: the
 * « Intervenant » select is disabled with a loading placeholder while the
 * claim's intervenants load, and with an explanatory helper text once their
 * fetch failed; every other field is disabled until an intervenant is loaded
 * (and every field after a successful save — the recap is read-only).
 */
function withRuntimeState(
  config: EntityFormConfig,
  optionsState: OptionsLoadState,
  recordReady: boolean,
  submitted: boolean,
): EntityFormConfig {
  const fields: Record<string, FieldConfig> = {};
  for (const [key, field] of Object.entries(config.fields)) {
    if (key === ID_SINP_FIELD_KEY) {
      if (submitted) {
        fields[key] = { ...field, disabled: true };
      } else if (optionsState === 'loading') {
        fields[key] = {
          ...field,
          disabled: true,
          options: {
            ...(field.options as SelectFieldOptions | undefined),
            placeholder: EDIT_INTERVENANT_CARD_TEXT.optionsLoadingPlaceholder,
          } as SelectFieldOptions,
        };
      } else if (optionsState === 'error') {
        fields[key] = {
          ...field,
          disabled: true,
          helperText: EDIT_INTERVENANT_CARD_TEXT.optionsLoadError,
        };
      } else {
        fields[key] = field;
      }
    } else {
      fields[key] = recordReady && !submitted ? field : { ...field, disabled: true };
    }
  }
  return { ...config, fields };
}

// ---------------------------------------------------------------------------
// Value helpers
// ---------------------------------------------------------------------------

/** A form / flux value as the text the workflow expects (`''` when empty). */
function toText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

/** The `ID_SINP` held by the « Intervenant » select, `null` when none. */
function parseIdSinp(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
}

/**
 * The form values of a loaded intervenant: the select keeps its id, every
 * editable field takes the flux value (`''` when `null`). « Téléphone » is the
 * flux's `telephoneMobile` ONLY — the save writes it back there and replays
 * `telephoneFixe` unchanged, so a landline fallback would copy the fixe
 * number into `telephoneMobile` on an otherwise untouched save.
 */
function toFormValues(idSinp: number, record: SinperRecord): Record<string, string> {
  const values: Record<string, string> = { [ID_SINP_FIELD_KEY]: String(idSinp) };
  for (const key of INTERVENANT_UPDATE_FIELDS) {
    values[key] = toText(key === 'telephone' ? record.telephoneMobile : record[key]);
  }
  return values;
}

/** "NOM Prénom" of an intervenant (recap + fallback option label). */
function displayName(nom: unknown, prenom: unknown): string {
  return [toText(nom).trim(), toText(prenom).trim()].filter(Boolean).join(' ');
}

/** Step trail of a failed workflow run (see `WorkflowExecutionError`). */
function readStepExecutions(error: unknown): WorkflowStepExecutionSummary[] {
  if (!(error instanceof Error) || !('stepExecutions' in error)) return [];
  const steps = (error as WorkflowExecutionError).stepExecutions;
  return Array.isArray(steps) ? steps : [];
}

/**
 * One intervenant load, tagged with the selection it was made for — the
 * selected `idSinp` and the `reloadKey` of the time. The card derives its
 * loading / ready / error state by matching the latest outcomes against the
 * current selection, so no state is set synchronously when a load starts.
 */
interface IntervenantLoad {
  idSinp: number;
  reloadKey: number;
}

interface LoadedIntervenant extends IntervenantLoad {
  record: SinperRecord;
}

interface FailedIntervenantLoad extends IntervenantLoad {
  message: string;
}

function matchesSelection(
  load: IntervenantLoad | null,
  idSinp: number | null,
  reloadKey: number,
): boolean {
  return load !== null && load.idSinp === idSinp && load.reloadKey === reloadKey;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * « Modifier un intervenant » (mahhp 22919): pick one of the claim's
 * intervenants, which preloads its `/sinper/{id_sinp}` flux into the same
 * fields as the create form, edit, and save through the
 * `claimflow-update-stakeholder` workflow.
 *
 * Preload: the loaded record becomes the form's baseline through
 * `useEntityForm`'s `reset(values)` (new initial values — the form is not
 * dirty after a load, and switching intervenant clears stale errors). No
 * remount, so the card keeps its DOM anchor.
 *
 * It also honours `useClaimDataStore().editIntervenantRequest`
 * (`requestEditIntervenant(idSinp)`): each new request selects that
 * intervenant (re-fetching the options when it is not listed) and (re)loads
 * it, leaving the "modified" recap if needed.
 *
 * Keyed on the claim: another claim (a params-only navigation) starts from a
 * fresh card — no intervenant of the previous claim stays selected.
 */
export function EditIntervenantCard(props: Readonly<EditIntervenantCardProps>) {
  return <EditIntervenantCardInner key={props.idSin} {...props} />;
}

function EditIntervenantCardInner({
  idSin,
  _layoutOverrides,
}: Readonly<EditIntervenantCardProps>) {
  // Collapsed on first paint — every card in the claim view's right column
  // opens folded. Driven by state + `onExpandChange` rather than a literal
  // `defaultExpanded={false}`, because `Card` re-applies a literal default on
  // every parent re-render, which would fold the card again mid-edit.
  const [expanded, setExpanded] = useState(false);

  const [options, setOptions] = useState<IntervenantOption[]>([]);
  const [optionsState, setOptionsState] = useState<OptionsLoadState>('loading');
  // Bumped to re-fetch the options (a request for an unlisted intervenant).
  const [optionsRefresh, setOptionsRefresh] = useState(0);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);

  // The id held by the « Intervenant » select. The form config (built before
  // `useEntityForm`) needs it to know whether the loaded record still matches
  // the selection, so it is mirrored in state — adjusted during render right
  // after the form is created (the React "derive from a changed value"
  // pattern; one extra render per selection change).
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Set to a store request's nonce to (re)load its intervenant — even the one
  // already selected.
  const [reloadKey, setReloadKey] = useState(0);
  // The intervenant a store request asked for, until the select holds it.
  const [pendingSelection, setPendingSelection] = useState<number | null>(null);
  const [loaded, setLoaded] = useState<LoadedIntervenant | null>(null);
  const [failedLoad, setFailedLoad] = useState<FailedIntervenantLoad | null>(null);

  const recordReady = matchesSelection(loaded, selectedId, reloadKey);
  const recordError =
    !recordReady && matchesSelection(failedLoad, selectedId, reloadKey)
      ? (failedLoad?.message ?? null)
      : null;
  const loadingRecord = selectedId !== null && !recordReady && recordError === null;

  const [submittedLabel, setSubmittedLabel] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [failedSteps, setFailedSteps] = useState<WorkflowStepExecutionSummary[]>([]);

  // Latest form API for the effects' async loaders (synced after each render).
  const formApiRef = useRef<UseEntityFormReturn | null>(null);
  // Recap label of the save in flight ("NOM Prénom").
  const savedLabelRef = useRef('');

  // -- Store request: edit a given intervenant -----------------------------
  //
  // Adopted during render (like `CloseTaskCard`'s closed-task signal): leave
  // the recap, remember which intervenant the select must move to, force its
  // (re)load and re-fetch the options when it is not listed. The effect below
  // then consumes the request and moves the select (external stores only).
  const editRequest = useClaimDataStore((s) => s.editIntervenantRequest);
  const [adoptedRequestNonce, setAdoptedRequestNonce] = useState<number | null>(null);
  if (editRequest !== null && editRequest.nonce !== adoptedRequestNonce) {
    setAdoptedRequestNonce(editRequest.nonce);
    setSubmittedLabel(null);
    setSaveError(null);
    setFailedSteps([]);
    setPendingSelection(editRequest.idSinp);
    setReloadKey(editRequest.nonce);
    if (!options.some((option) => option.value === editRequest.idSinp)) {
      setOptionsRefresh((count) => count + 1);
    }
  }

  // -- Options -------------------------------------------------------------

  // Re-fetched when the claim's intervenants change (one added by
  // `IntervenantForm`, or renamed by this card's own save) and on demand. A
  // failed refresh keeps a loaded list usable; the error only shows when no
  // list ever loaded.
  const intervenantsVersion = useClaimDataStore((s) => s.intervenantsVersion);
  useEffect(() => {
    let cancelled = false;
    async function loadOptions(): Promise<void> {
      try {
        const rows = await intervenantService.fetchIntervenantOptions(idSin);
        if (cancelled) return;
        setOptions(rows);
        setOptionsState('ready');
      } catch {
        if (cancelled) return;
        setOptionsState((state) => (state === 'ready' ? state : 'error'));
      }
    }
    void loadOptions();
    return () => {
      cancelled = true;
    };
  }, [idSin, intervenantsVersion, optionsRefresh]);

  // Compagnie options — `boby-ws2-gs-sel-compagnie`, like `IntervenantForm`.
  // A failure is silent: the select stays empty.
  useEffect(() => {
    let cancelled = false;
    async function loadCompanies(): Promise<void> {
      try {
        const rows = await intervenantService.fetchCompanyOptions();
        if (!cancelled) setCompanies(rows);
      } catch {
        // silently fail — the Compagnie select stays empty
      }
    }
    void loadCompanies();
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep a loaded intervenant selectable even when `WS2_GS_SEL_IDSINP` does
  // not list it (yet).
  const selectOptions = useMemo(() => {
    if (loaded === null || options.some((option) => option.value === loaded.idSinp)) {
      return options;
    }
    return [
      ...options,
      {
        value: loaded.idSinp,
        label: displayName(loaded.record.nom, loaded.record.prenom) || String(loaded.idSinp),
      },
    ];
  }, [options, loaded]);

  // -- Form ----------------------------------------------------------------

  const baseConfig = useMemo(
    () => buildEditIntervenantFormConfig(selectOptions, companies),
    [selectOptions, companies],
  );

  usePublishLayoutConfig(
    EDIT_INTERVENANT_COMPONENT_ID,
    buildPublishedConfigFromEntityForm(baseConfig, EDIT_INTERVENANT_CARD_TEXT.title),
  );

  const mergedConfig = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  const submitted = submittedLabel !== null;

  const formConfig = useMemo(
    () => withRuntimeState(mergedConfig, optionsState, recordReady, submitted),
    [mergedConfig, optionsState, recordReady, submitted],
  );

  /**
   * Save: `id_sinp` of the loaded intervenant + every editable field. The
   * payload only carries visible fields, so a field hidden by a tenant rule
   * keeps its loaded value (it is sent back unchanged, never cleared).
   */
  const mutationFn = useCallback(
    (payload: Record<string, unknown> | FormData) => {
      setSaveError(null);
      setFailedSteps([]);
      // Never save while the selection and the loaded record disagree (an
      // intervenant is still loading) — the button is disabled then anyway.
      if (loaded === null || !recordReady) {
        return Promise.reject(new Error(EDIT_INTERVENANT_CARD_TEXT.saveErrorFallback));
      }
      const visible =
        payload instanceof FormData ? Object.fromEntries(payload.entries()) : payload;
      const values: Record<string, unknown> = {
        ...toFormValues(loaded.idSinp, loaded.record),
        ...visible,
      };
      const fields: IntervenantUpdateValues = {};
      for (const key of INTERVENANT_UPDATE_FIELDS) {
        fields[key] = toText(values[key]);
      }
      savedLabelRef.current = displayName(fields.nom, fields.prenom) || String(loaded.idSinp);
      return intervenantService.updateIntervenant(loaded.idSinp, fields);
    },
    [loaded, recordReady],
  );

  const handleSuccess = useCallback(() => {
    setSubmittedLabel(savedLabelRef.current);
    // Folded recap, like `IntervenantForm` after an add.
    setExpanded(false);
    // The claim's intervenants changed — cards deriving data from them (the
    // Email card's recipient suggestions, this card's own select labels)
    // re-fetch (mahhp 22850).
    useClaimDataStore.getState().bumpIntervenants();
  }, []);

  const handleError = useCallback((e: unknown) => {
    setSaveError(e instanceof Error ? e.message : EDIT_INTERVENANT_CARD_TEXT.saveErrorFallback);
    setFailedSteps(readStepExecutions(e));
  }, []);

  const form = useEntityForm({
    config: formConfig,
    mutationFn,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const selectedValue = parseIdSinp(form.getValue(ID_SINP_FIELD_KEY));
  if (selectedValue !== selectedId) {
    setSelectedId(selectedValue);
  }
  if (pendingSelection !== null && selectedValue === pendingSelection) {
    setPendingSelection(null);
  }

  useEffect(() => {
    formApiRef.current = form;
  });

  // Consume the adopted store request and move the select to its intervenant.
  useEffect(() => {
    if (editRequest === null) return;
    useClaimDataStore.getState().consumeEditIntervenant(editRequest.nonce);
    formApiRef.current?.setValue(ID_SINP_FIELD_KEY, String(editRequest.idSinp));
  }, [editRequest]);

  // -- Preload the selected intervenant -----------------------------------

  useEffect(() => {
    if (selectedId === null) return undefined;
    // A store request is moving the select — load once it holds the target.
    if (pendingSelection !== null && pendingSelection !== selectedId) return undefined;

    let cancelled = false;
    const idSinp = selectedId;
    async function loadIntervenant(): Promise<void> {
      try {
        const record = await intervenantService.fetchIntervenant(idSinp);
        if (cancelled) return;
        // The loaded record becomes the form's baseline (new initial values).
        formApiRef.current?.reset(toFormValues(idSinp, record));
        setLoaded({ idSinp, reloadKey, record });
      } catch (e) {
        if (cancelled) return;
        setFailedLoad({
          idSinp,
          reloadKey,
          message:
            e instanceof Error && e.message !== ''
              ? e.message
              : EDIT_INTERVENANT_CARD_TEXT.recordLoadErrorFallback,
        });
      }
    }
    void loadIntervenant();
    return () => {
      // A newer selection (or the unmount) invalidates this load.
      cancelled = true;
    };
  }, [selectedId, reloadKey, pendingSelection]);

  // -- Leave the recap for a fresh edit form -------------------------------

  const handleEditAnother = useCallback(() => {
    setSubmittedLabel(null);
    setSaveError(null);
    setFailedSteps([]);
    setLoaded(null);
    setFailedLoad(null);
    formApiRef.current?.reset({});
  }, []);

  // -- Render --------------------------------------------------------------

  const title = resolveCardTitle(EDIT_INTERVENANT_CARD_TEXT.title, _layoutOverrides);

  if (submittedLabel !== null) {
    return (
      <Card id={actionAnchorId(EDIT_INTERVENANT_COMPONENT_ID)}>
        <Card.Header collapsible defaultExpanded={expanded} onExpandChange={setExpanded}>
          <Icon icon={CheckCircle2} size="md" color="success" />
          <Text as="span" weight="semibold" size="base">
            {title}
          </Text>
          <Text as="span" size="sm" color="muted" className="ml-2">
            ({submittedLabel})
          </Text>
        </Card.Header>
        <Card.Content>
          <div className="pb-4">
            <Button
              variant="secondary"
              leftIcon={<RotateCcw className="w-4 h-4" />}
              onClick={handleEditAnother}
              className="w-full"
            >
              {EDIT_INTERVENANT_CARD_TEXT.editAnother}
            </Button>
          </div>
          <EntityForm config={formConfig} form={form}>
            <EntityForm.Fields />
            <div className="pt-4">
              <Button
                variant="primary"
                leftIcon={<Save className="w-4 h-4" />}
                disabled
                className="w-full"
              >
                {EDIT_INTERVENANT_CARD_TEXT.submitted}
              </Button>
            </div>
          </EntityForm>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card id={actionAnchorId(EDIT_INTERVENANT_COMPONENT_ID)}>
      <Card.Header
        collapsible
        defaultExpanded={expanded}
        onExpandChange={setExpanded}
        action={loadingRecord ? <Spinner size="sm" /> : undefined}
      >
        <Icon icon={UserPen} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {title}
        </Text>
      </Card.Header>
      <Card.Content>
        <EntityForm config={formConfig} form={form}>
          <EntityForm.Fields />
          {recordError && (
            <div className="pt-4">
              <Alert variant="error" title={EDIT_INTERVENANT_CARD_TEXT.recordLoadErrorTitle}>
                {recordError}
              </Alert>
            </div>
          )}
          {saveError && (
            <div className="pt-4">
              <Alert variant="error" title={EDIT_INTERVENANT_CARD_TEXT.saveErrorTitle}>
                {saveError}
              </Alert>
            </div>
          )}
          {failedSteps.length > 0 && (
            <div className="pt-4">
              <Text size="sm" weight="semibold" className="mb-2">
                {EDIT_INTERVENANT_CARD_TEXT.stepsHeading}
              </Text>
              <WorkflowStepTracker steps={failedSteps} />
            </div>
          )}
          {formConfig.actions?.submit?.visible !== false && (
            <div className="pt-4">
              <Button
                type="submit"
                variant="primary"
                leftIcon={<Save className="w-4 h-4" />}
                loading={form.isSubmitting}
                disabled={form.isSubmitting || !recordReady}
                className="w-full"
              >
                {formConfig.actions?.submit?.label ?? EDIT_INTERVENANT_CARD_TEXT.submit}
              </Button>
            </div>
          )}
        </EntityForm>
      </Card.Content>
    </Card>
  );
}
